import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const API_URL = '/api';
const PAGE_SIZE = 20;

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
};

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const GAME_TYPE_ICON = {
    coinflip: '⚡',
    battle: '⚔',
    blackjack: '🃏',
};

const outcomeClasses = {
    win: 'text-cyber-cyan glow-cyan font-bold',
    lose: 'text-cyber-pink glow-pink',
    tie: 'text-yellow-400',
    cancelled: 'text-cyber-dim',
};

const deltaClasses = {
    positive: 'text-cyber-cyan glow-cyan',
    negative: 'text-cyber-pink glow-pink',
    zero: 'text-cyber-dim',
};

export default function GamesHistory() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = async (nextOffset) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: String(nextOffset),
            });
            const res = await fetch(`${API_URL}/profile/games?${params.toString()}`, {
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('profile.games.failLoad'));
            setRows(data.rows || []);
            setCount(data.count || 0);
            setOffset(nextOffset);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(0);
        // eslint-disable-next-line
    }, []);

    const coinLabel = (v) => {
        if (v === 0) return `☠ ${t('game.skull')}`;
        if (v === 1) return `⚡ ${t('game.bolt')}`;
        return '—';
    };

    const outcomeLabel = (o) => {
        switch (o) {
            case 'win': return t('profile.games.win');
            case 'lose': return t('profile.games.lose');
            case 'tie': return t('profile.games.tie');
            case 'cancelled': return t('profile.games.cancelled');
            default: return o;
        }
    };

    const deltaText = (delta) => {
        if (delta > 0) return `+${delta}¢`;
        if (delta < 0) return `${delta}¢`;
        return '—';
    };

    const deltaClass = (delta) => {
        if (delta > 0) return deltaClasses.positive;
        if (delta < 0) return deltaClasses.negative;
        return deltaClasses.zero;
    };

    const canPrev = offset > 0;
    const canNext = offset + rows.length < count;

    if (loading && rows.length === 0) {
        return (
            <div className="text-center py-10 text-cyber-dim">
                <div className="font-display tracking-widest text-sm animate-pulse">
                    {t('profile.games.loading')}
                </div>
            </div>
        );
    }

    if (!loading && rows.length === 0) {
        return (
            <div className="text-center py-10 text-cyber-dim">
                <div className="font-display tracking-widest text-sm mb-2">{t('profile.games.empty')}</div>
                <Link to="/game" className="neon-btn text-xs inline-block mt-2">
                    {t('profile.games.emptyAction')}
                </Link>
            </div>
        );
    }

    return (
        <div>
            <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                <table className="w-full text-xs sm:text-sm min-w-[640px]">
                    <thead>
                        <tr className="text-left text-cyber-dim tracking-widest text-[10px] sm:text-xs uppercase">
                            <th className="py-2 pr-2 sm:pr-4">{t('profile.games.date')}</th>
                            <th className="py-2 pr-2 sm:pr-4">{t('profile.games.type')}</th>
                            <th className="py-2 pr-2 sm:pr-4">{t('profile.games.opponent')}</th>
                            <th className="py-2 pr-2 sm:pr-4">{t('profile.games.bet')}</th>
                            <th className="py-2 pr-2 sm:pr-4">{t('profile.games.choice')}</th>
                            <th className="py-2 pr-2 sm:pr-4">{t('profile.games.outcome')}</th>
                            <th className="py-2 pr-2 sm:pr-4">{t('profile.games.delta')}</th>
                            <th className="py-2 pr-2 sm:pr-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const dateValue = row.completedAt || row.createdAt;
                            const opponentName = row.opponent?.name || '—';
                            const isCoinflip = row.gameType === 'coinflip';
                            const choiceCell = isCoinflip && row.myChoice != null
                                ? (
                                    <span>
                                        {coinLabel(row.myChoice)}
                                        {row.coinResult != null && row.status === 'completed' && (
                                            <span className="text-cyber-dim"> → {coinLabel(row.coinResult)}</span>
                                        )}
                                    </span>
                                )
                                : '—';
                            return (
                                <tr key={row.id} className="border-t border-cyber-border/40">
                                    <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim text-[10px] sm:text-xs whitespace-nowrap">
                                        {formatDate(dateValue)}
                                    </td>
                                    <td className="py-2 pr-2 sm:pr-4">
                                        <span className="mr-1">{GAME_TYPE_ICON[row.gameType] || '•'}</span>
                                        <span className="font-display tracking-wider text-cyber-text">
                                            {t(`game.type.${row.gameType}`, { defaultValue: row.gameType })}
                                        </span>
                                    </td>
                                    <td className="py-2 pr-2 sm:pr-4 font-display text-cyber-text tracking-wider truncate max-w-[120px]">
                                        {opponentName}
                                    </td>
                                    <td className="py-2 pr-2 sm:pr-4 font-display text-cyber-purple glow-purple">
                                        {row.betAmount}¢
                                    </td>
                                    <td className="py-2 pr-2 sm:pr-4 font-mono text-[10px] sm:text-xs">
                                        {choiceCell}
                                    </td>
                                    <td className={`py-2 pr-2 sm:pr-4 font-display tracking-wider ${outcomeClasses[row.outcome] || ''}`}>
                                        {outcomeLabel(row.outcome)}
                                    </td>
                                    <td className={`py-2 pr-2 sm:pr-4 font-mono ${deltaClass(row.betDelta)}`}>
                                        {deltaText(row.betDelta)}
                                    </td>
                                    <td className="py-2 pr-2 sm:pr-4">
                                        {isCoinflip && row.status === 'completed' ? (
                                            <Link
                                                to={`/verify/${row.id}`}
                                                className="text-cyan-400 hover:underline text-[10px] sm:text-xs tracking-widest"
                                            >
                                                {t('profile.games.verify')}
                                            </Link>
                                        ) : (
                                            <span className="text-cyber-dim text-[10px] sm:text-xs tracking-widest">
                                                {t('profile.games.verifyNA')}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[10px] text-cyber-dim tracking-widest">
                    {t('profile.games.pagination', {
                        from: offset + 1,
                        to: Math.min(offset + rows.length, count),
                        total: count,
                    })}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
                        disabled={!canPrev || loading}
                        className="neon-btn text-[10px] px-2 sm:px-3 py-1"
                    >
                        {t('profile.games.prev')}
                    </button>
                    <button
                        onClick={() => load(offset + PAGE_SIZE)}
                        disabled={!canNext || loading}
                        className="neon-btn text-[10px] px-2 sm:px-3 py-1"
                    >
                        {t('profile.games.next')}
                    </button>
                </div>
            </div>
        </div>
    );
}
